import Loader from "components/Loader/loader"
import moment from "moment"
import React, { useEffect, useState } from "react"
import { FaPlus, FaEdit } from "react-icons/fa"
import { MdCancel, MdDelete, MdEdit } from "react-icons/md"
import Select from "react-select"
import {
  Button,
  Card,
  CardBody,
  Col,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Table,
} from "reactstrap"
import Swal from "sweetalert2"
import {
  FetchDeleteData,
  fetchData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"

const WEEK_PRESET_OPTIONS = [1, 2, 3, 4, 5]

const days = [
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
  { label: "Saturday", value: 6 },
  { label: "Sunday", value: 0 },
]

const formatTo12Hour = time => {
  if (!time) return ""
  const [hours, minutes] = time.split(":")
  let hour = parseInt(hours, 10)
  const ampm = hour >= 12 ? "PM" : "AM"
  hour = hour % 12
  if (hour === 0) hour = 12
  return `${hour}:${minutes} ${ampm}`
}

// Contractual slots live in the LocationSlot table, which stores each
// week's own explicit date range rather than an abstract cycling number.
// "Week N" always means the N-th 7-day block from the contract's start
// date, clipped to the contract's end date.
const computeWeekRange = (weekNumber, startDate, endDate) => {
  const weekStart = moment(startDate).add((Number(weekNumber) - 1) * 7, "days")
  const weekEnd = moment.min(weekStart.clone().add(6, "days"), moment(endDate))
  return {
    weekStart: weekStart.format("YYYY-MM-DD"),
    weekEnd: weekEnd.format("YYYY-MM-DD"),
  }
}

// LocationSlot rows use different field names (week/day as plain strings)
// than LocationWeekPlanning (weekNo/dayOfWeek) — normalize to the shape the
// rest of this component already expects, so only the fetch/save/delete
// calls below need to know which table contractual actually uses.
const normalizeSlotRows = rows =>
  (rows || []).map(s => ({
    ...s,
    weekNo: Number(s.week),
    dayOfWeek: Number(s.day),
  }))

const formatDuration = (inTime, outTime) => {
  if (!inTime || !outTime) return ""
  const start = moment(inTime, "HH:mm")
  const end = moment(outTime, "HH:mm")
  const totalMinutes = Math.max(0, end.diff(start, "minutes"))
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  return `${h}h ${m}m`
}

const LocationTimeModal = ({
  isOpen,
  toggle,
  selectedLocation,
  locationName,
  fetchLocations,
  startDate,
  endDate = "",
  employmentType,
}) => {
  const empType = employmentType || "contractual"
  const isOngoing = empType === "ongoing"

  // Shared entry fields (used by both the ongoing container flow and the
  // fixed-term single-form flow below)
  const [day, setday] = useState("")
  const [inTime, setInTime] = useState("09:00")
  const [outTime, setOutTime] = useState("19:00")
  const [employees, setEmployees] = useState([])
  const [uniqueEmployees, setUniqueEmployees] = useState([])
  const [allAvailableEmployees, setAllAvailableEmployees] = useState([])
  const [slots, setSlots] = useState([])
  const [editId, setEditId] = useState(null)
  const [loading, setloading] = useState(false)
  const [errors, setErrors] = useState({
    weekNo: "",
    day: "",
    inTime: "",
    outTime: "",
    employees: "",
    sourceWeekNo: "",
  })

  // Ongoing-only: which week container currently has its fields open
  const [activeWeekNo, setActiveWeekNo] = useState(null)

  // Fixed-term-only
  const [weekNo, setWeekNo] = useState("")
  const [customWeekNo, setCustomWeekNo] = useState("")
  const [copyWeekEnabled, setCopyWeekEnabled] = useState(false)
  const [sourceWeekNo, setSourceWeekNo] = useState("")

  // Ongoing-only: copy/use-existing controls inside the active week's card
  const [ongoingCopyEnabled, setOngoingCopyEnabled] = useState(false)
  const [ongoingSourceWeekNo, setOngoingSourceWeekNo] = useState("")

  const fieldsDisabled = copyWeekEnabled || ongoingCopyEnabled
  const dayDisabled = fieldsDisabled
  const effectiveWeekNo = weekNo === "custom" ? customWeekNo : weekNo

  const resetEntryFields = () => {
    setday("")
    setInTime("09:00")
    setOutTime("19:00")
    setEmployees([])
    setErrors({
      weekNo: "",
      day: "",
      inTime: "",
      outTime: "",
      employees: "",
      sourceWeekNo: "",
    })
  }

  const resetForm = () => {
    setActiveWeekNo(null)
    setWeekNo("")
    setCustomWeekNo("")
    setCopyWeekEnabled(false)
    setSourceWeekNo("")
    setOngoingCopyEnabled(false)
    setOngoingSourceWeekNo("")
    setEditId(null)
    setUniqueEmployees([])
    setAllAvailableEmployees([])
    resetEntryFields()
  }

  useEffect(() => {
    if (selectedLocation) {
      getLocationSlots()
      resetForm()
    }
    // eslint-disable-next-line
  }, [selectedLocation])

  async function getLocationSlots() {
    setloading(true)
    const APIdata = await fetchData(
      setloading,
      isOngoing
        ? `location-week-planning/${selectedLocation}`
        : `slot/${selectedLocation}`
    )
    if (APIdata?.success) {
      setSlots(isOngoing ? APIdata?.data || [] : normalizeSlotRows(APIdata?.data))
    } else {
      setSlots([])
    }
    setloading(false)
  }

  useEffect(() => {
    if (!isOpen) return
    if (day !== "" && inTime && outTime) {
      getLocationEmployees()
    } else {
      setUniqueEmployees([])
    }
    // eslint-disable-next-line
  }, [day, inTime, outTime, isOpen])

  async function getLocationEmployees() {
    const APIdata = await FetchPostData(
      setloading,
      `slot/available/employees`,
      {
        day: Number(day),
        inTime,
        outTime,
        locationId: selectedLocation,
      }
    )
    if (APIdata?.data?.success) {
      setAllAvailableEmployees(APIdata?.data?.employees || [])
      setUniqueEmployees(APIdata?.data?.employees || [])
    } else {
      setAllAvailableEmployees([])
      setUniqueEmployees([])
    }
  }

  // ---------------------------------------------------------------------
  // Ongoing (no end date) flow: one container per week, auto-numbered,
  // only "Add Week" + the active container's "Save" button are shown.
  // ---------------------------------------------------------------------

  const savedWeekNumbers = [...new Set(slots.map(s => Number(s.weekNo)))].sort(
    (a, b) => a - b
  )
  const containerWeekNumbers =
    activeWeekNo && !savedWeekNumbers.includes(activeWeekNo)
      ? [...savedWeekNumbers, activeWeekNo].sort((a, b) => a - b)
      : savedWeekNumbers

  const handleAddWeekContainer = () => {
    const nextWeekNo = savedWeekNumbers.length
      ? Math.max(...savedWeekNumbers) + 1
      : 1
    setActiveWeekNo(nextWeekNo)
    setEditId(null)
    setOngoingCopyEnabled(false)
    setOngoingSourceWeekNo("")
    resetEntryFields()
  }

  const handleEditOngoingRow = row => {
    setActiveWeekNo(Number(row.weekNo))
    setEditId(row.id)
    setday(row.dayOfWeek)
    setInTime(row.inTime)
    setOutTime(row.outTime)
    setEmployees(
      row?.employees?.map(emp => ({ value: emp.id, label: emp.name })) || []
    )
    setOngoingCopyEnabled(false)
    setOngoingSourceWeekNo("")
    setErrors({})
  }

  const handleCancelOngoingRow = () => {
    setEditId(null)
    setOngoingCopyEnabled(false)
    setOngoingSourceWeekNo("")
    resetEntryFields()
  }

  const validateOngoingRow = () => {
    const newErrors = {}

    if (ongoingCopyEnabled) {
      if (!ongoingSourceWeekNo || ongoingSourceWeekNo === "") {
        newErrors.sourceWeekNo = "Please select a week to copy from"
      }
      setErrors(newErrors)
      return Object.keys(newErrors).length === 0
    }

    if (day === "" || day === null || day === undefined) {
      newErrors.day = "Please select a day"
    }
    if (!inTime) {
      newErrors.inTime = "Please select in time"
    }
    if (!outTime) {
      newErrors.outTime = "Please select out time"
    } else if (inTime && outTime <= inTime) {
      newErrors.outTime = "Out time must be after in time"
    }
    if (!employees || employees.length === 0) {
      newErrors.employees = "Please select at least one employee"
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSaveOngoingRow = async () => {
    if (!validateOngoingRow()) return

    try {
      if (ongoingCopyEnabled) {
        const result = await FetchPostData(
          setloading,
          "location-week-planning/copy-week",
          {
            locationId: selectedLocation,
            sourceWeekNo: Number(ongoingSourceWeekNo),
            targetWeekNo: activeWeekNo,
          }
        )

        if (result?.data?.success) {
          const { successCount, skipCount, errorCount, errors: copyErrors } =
            result.data
          let msg = `${successCount} entr${successCount === 1 ? "y" : "ies"} copied to Week ${activeWeekNo}.`
          if (skipCount > 0) msg += ` ${skipCount} skipped (conflict).`
          if (errorCount > 0)
            msg += ` ${errorCount} failed: ${copyErrors?.join("; ")}`

          Swal.fire({
            title: "Week Copied!",
            text: msg,
            icon: successCount > 0 ? "success" : "warning",
            timer: 3000,
          })

          setSlots(result?.data?.allPlannings || [])
          setOngoingCopyEnabled(false)
          setOngoingSourceWeekNo("")
          fetchLocations?.()
        } else {
          throw new Error(result?.data?.message || "Failed to copy week")
        }
        return
      }

      const payload = {
        weekNo: activeWeekNo,
        dayOfWeek: Number(day),
        inTime,
        outTime,
        employees: employees.map(e => e.value),
      }

      let APIData
      if (editId) {
        APIData = await FetchPutData(
          setloading,
          "location-week-planning",
          editId,
          payload
        )
      } else {
        APIData = await FetchPostData(setloading, "location-week-planning", {
          ...payload,
          locationId: selectedLocation,
        })
      }

      if (APIData?.data?.success) {
        Swal.fire({
          title: editId
            ? "Time slot updated successfully!"
            : "Time slot created successfully!",
          icon: "success",
          timer: 2000,
        })
        setSlots(APIData?.data?.allPlannings || [])
        setEditId(null)
        resetEntryFields()
        fetchLocations?.()
      } else {
        throw new Error(
          APIData?.data?.message ||
            APIData?.response?.data?.message ||
            "Unknown error"
        )
      }
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: err.message || "Failed to save week planning. Please try again.",
        icon: "error",
      })
    }
  }

  // ---------------------------------------------------------------------
  // Fixed-term (contractual) flow: unchanged single-form + full table,
  // including Week dropdown / Copy Week.
  // ---------------------------------------------------------------------

  const validateInputs = () => {
    const newErrors = {}

    if (copyWeekEnabled) {
      if (!sourceWeekNo || sourceWeekNo === "") {
        newErrors.sourceWeekNo = "Please select a week to copy from"
      }
      if (!effectiveWeekNo) {
        newErrors.weekNo = "Please select a destination week"
      }
      setErrors(newErrors)
      return Object.keys(newErrors).length === 0
    }

    if (!effectiveWeekNo || Number(effectiveWeekNo) < 1) {
      newErrors.weekNo = "Please select a week"
    }

    if (day === "" || day === null || day === undefined) {
      newErrors.day = "Please select a day"
    }

    if (!inTime) {
      newErrors.inTime = "Please select in time"
    }

    if (!outTime) {
      newErrors.outTime = "Please select out time"
    } else if (inTime && outTime <= inTime) {
      newErrors.outTime = "Out time must be after in time"
    }

    if (!employees || employees.length === 0) {
      newErrors.employees = "Please select at least one employee"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleAddOrUpdateSlot = async () => {
    if (!validateInputs()) {
      return
    }

    try {
      if (copyWeekEnabled) {
        const destRange = computeWeekRange(effectiveWeekNo, startDate, endDate)
        const copyPayload = {
          locationId: selectedLocation,
          sourceWeek: String(sourceWeekNo),
          destinationWeek: String(effectiveWeekNo),
          destinationWeekStart: destRange.weekStart,
          destinationWeekEnd: destRange.weekEnd,
        }

        const result = await FetchPostData(
          setloading,
          "slot/copy-week",
          copyPayload
        )

        if (result?.data?.success) {
          const { successCount, skipCount, errorCount, errors: copyErrors } =
            result.data
          let msg = `${successCount} entr${successCount === 1 ? "y" : "ies"} copied to Week ${effectiveWeekNo}.`
          if (skipCount > 0) msg += ` ${skipCount} skipped (conflict).`
          if (errorCount > 0)
            msg += ` ${errorCount} failed: ${copyErrors?.join("; ")}`

          Swal.fire({
            title: "Week Copied!",
            text: msg,
            icon: successCount > 0 ? "success" : "warning",
            timer: 3000,
          })

          setSlots(normalizeSlotRows(result?.data?.allSlots))
          resetForm()
          fetchLocations?.()
        } else {
          throw new Error(result?.data?.message || "Failed to copy week")
        }
      } else {
        const weekRange = computeWeekRange(effectiveWeekNo, startDate, endDate)
        const payload = {
          week: String(effectiveWeekNo),
          day: Number(day),
          inTime,
          outTime,
          employees: JSON.stringify(employees.map(e => e.value)),
          weekStart: weekRange.weekStart,
          weekEnd: weekRange.weekEnd,
        }

        let APIData
        if (editId) {
          APIData = await FetchPutData(setloading, "slot", editId, payload)
        } else {
          APIData = await FetchPostData(setloading, "slot", {
            ...payload,
            locationId: selectedLocation,
          })
        }

        if (APIData?.data?.success) {
          Swal.fire({
            title: editId
              ? "Time slot updated successfully!"
              : "Time slot created successfully!",
            icon: "success",
            timer: 2000,
          })
          setSlots(normalizeSlotRows(APIData?.data?.allSlots))
          resetForm()
          fetchLocations?.()
        } else {
          throw new Error(
            APIData?.data?.message ||
              APIData?.response?.data?.message ||
              "Unknown error"
          )
        }
      }
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: err.message || "Failed to save week planning. Please try again.",
        icon: "error",
      })
    }
  }

  const handleEditSlot = slot => {
    if (WEEK_PRESET_OPTIONS.includes(Number(slot.weekNo))) {
      setWeekNo(Number(slot.weekNo))
      setCustomWeekNo("")
    } else {
      setWeekNo("custom")
      setCustomWeekNo(slot.weekNo)
    }
    setday(slot.dayOfWeek)
    setInTime(slot.inTime)
    setOutTime(slot.outTime)
    setEditId(slot.id)
    setEmployees(
      slot?.employees?.map(emp => ({
        value: emp.id,
        label: emp.name,
      })) || []
    )
    setCopyWeekEnabled(false)
    setSourceWeekNo("")
    setErrors({})
  }

  const handleCancelEdit = () => {
    resetForm()
  }

  const existingWeekNumbers = savedWeekNumbers

  // ---------------------------------------------------------------------
  // Shared: delete (immediate) works the same for both flows
  // ---------------------------------------------------------------------

  const handleRemoveSlot = async id => {
    const confirmed = await Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this week planning entry?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    })

    if (!confirmed.isConfirmed) return

    try {
      const APIdata = await FetchDeleteData(
        setloading,
        isOngoing ? "location-week-planning" : "slot",
        id
      )
      if (APIdata?.data?.success) {
        Swal.fire({
          title: "Deleted!",
          text: "Week planning entry has been deleted.",
          icon: "success",
          timer: 2000,
        })
        setSlots(
          isOngoing
            ? APIdata?.data?.allPlannings || []
            : normalizeSlotRows(APIdata?.data?.allSlots)
        )
        if (editId === id) {
          setEditId(null)
          resetEntryFields()
        }
        fetchLocations?.()
      } else {
        throw new Error(
          APIdata?.data?.message || "Failed to delete week planning entry"
        )
      }
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: err.message || "Failed to delete. Please try again.",
        icon: "error",
      })
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} fullscreen>
      {loading && <Loader />}
      <ModalHeader toggle={toggle}>
        Set Location Time{" "}
        <span className="fw-bold ms-3">
          ({locationName}) (
          {endDate ? moment(startDate).format("DD/MM/YYYY") : ""}{" "}
          {endDate ? `- ${moment(endDate).format("DD/MM/YYYY")}` : "Continuous"}
          )
        </span>
      </ModalHeader>
      <ModalBody>
        {isOngoing ? (
          <>
            <Card className="mb-3 shadow-sm">
              <CardBody className="p-3 d-flex justify-content-between align-items-center">
                <h5 className="mb-0 text-primary">Add New Week Planning</h5>
                <Button
                  color="primary"
                  outline
                  size="sm"
                  className="rounded-4"
                  onClick={handleAddWeekContainer}
                >
                  <FaPlus className="me-1" size={10} />
                  Add Week
                </Button>
              </CardBody>
            </Card>

            {containerWeekNumbers.length === 0 && (
              <p className="text-muted">
                Click "Add Week" to start planning Week 1.
              </p>
            )}

            {containerWeekNumbers.map(wNo => {
              const rows = slots.filter(s => Number(s.weekNo) === wNo)
              const isActive = activeWeekNo === wNo

              return (
                <Card key={wNo} className="mb-3 shadow-sm">
                  <CardBody className="p-4">
                    <h6 className="text-secondary mb-3">Week {wNo}</h6>

                    {isActive && (
                      <Row className="align-items-end g-1 mb-3">
                        {!editId && savedWeekNumbers.some(w => w !== wNo) && (
                          <Col md="1" className="text-center">
                            <Label className="fw-medium mb-1 text-nowrap" style={{ fontSize: "12px" }}>
                              Copy
                            </Label>
                            <div
                              className="d-flex align-items-center justify-content-center rounded-4 border"
                              style={{
                                height: "34px",
                                backgroundColor: ongoingCopyEnabled ? "#e7f1ff" : "#fff",
                                borderColor: ongoingCopyEnabled ? "#0d6efd" : "#ced4da",
                                cursor: "pointer",
                              }}
                            >
                              <input
                                type="checkbox"
                                className="form-check-input m-0"
                                checked={ongoingCopyEnabled}
                                onChange={e => {
                                  setOngoingCopyEnabled(e.target.checked)
                                  if (!e.target.checked) setOngoingSourceWeekNo("")
                                  if (errors.sourceWeekNo)
                                    setErrors({ ...errors, sourceWeekNo: "" })
                                }}
                                style={{ width: 16, height: 16, cursor: "pointer" }}
                              />
                            </div>
                          </Col>
                        )}
                        {!editId && ongoingCopyEnabled && savedWeekNumbers.some(w => w !== wNo) && (
                          <Col md="2">
                            <Label className="fw-medium mb-1 text-nowrap" style={{ fontSize: "12px" }}>
                              Target Week <span className="text-danger">*</span>
                            </Label>
                            <select
                              value={ongoingSourceWeekNo}
                              onChange={e => {
                                setOngoingSourceWeekNo(e.target.value)
                                if (errors.sourceWeekNo)
                                  setErrors({ ...errors, sourceWeekNo: "" })
                              }}
                              className={`form-select rounded-4 ${errors.sourceWeekNo ? "is-invalid" : ""}`}
                              style={{ height: "34px", fontSize: "12px" }}
                            >
                              <option value="">Select</option>
                              {savedWeekNumbers
                                .filter(w => w !== wNo)
                                .map(w => (
                                  <option key={w} value={w}>
                                    Week {w}
                                  </option>
                                ))}
                            </select>
                            {errors.sourceWeekNo && (
                              <FormFeedback>{errors.sourceWeekNo}</FormFeedback>
                            )}
                          </Col>
                        )}
                        {!editId && savedWeekNumbers.some(w => w !== wNo) && (
                          <Col md="2">
                            <Label className="fw-medium mb-1 text-nowrap" style={{ fontSize: "12px" }}>
                              Use Existing
                            </Label>
                            <select
                              value=""
                              onChange={e => {
                                if (!e.target.value) return
                                const selected = slots.find(
                                  s => String(s.id) === e.target.value
                                )
                                if (!selected) return
                                setInTime(selected.inTime)
                                setOutTime(selected.outTime)
                                setEmployees(
                                  selected.employees?.map(emp => ({
                                    value: emp.id,
                                    label: emp.name,
                                  })) || []
                                )
                              }}
                              className="form-select rounded-4"
                              style={{ height: "34px", fontSize: "12px" }}
                            >
                              <option value="">Use Existing Slot…</option>
                              {slots.map(s => (
                                <option key={s.id} value={s.id}>
                                  Week {s.weekNo} - {days.find(d => d.value === s.dayOfWeek)?.label}
                                </option>
                              ))}
                            </select>
                          </Col>
                        )}
                        <Col md="1">
                          <Label className="fw-medium mb-1 text-nowrap" style={{ fontSize: "12px" }}>
                            Day <span className="text-danger">*</span>
                          </Label>
                          <select
                            value={day}
                            disabled={dayDisabled}
                            onChange={e => {
                              setday(e.target.value)
                              if (errors.day) setErrors({ ...errors, day: "" })
                            }}
                            className={`form-select rounded-4 ${errors.day ? "is-invalid" : ""}`}
                            style={{ height: "34px", fontSize: "12px" }}
                          >
                            <option value="">Select</option>
                            {days.map(d => (
                              <option key={d.value} value={d.value}>
                                {d.label}
                              </option>
                            ))}
                          </select>
                          {errors.day && <FormFeedback>{errors.day}</FormFeedback>}
                        </Col>
                        <Col md="1">
                          <Label className="fw-medium mb-1 text-nowrap" style={{ fontSize: "12px" }}>
                            In <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="time"
                            value={inTime}
                            disabled={fieldsDisabled}
                            onChange={e => {
                              setInTime(e.target.value)
                              if (errors.inTime) setErrors({ ...errors, inTime: "" })
                            }}
                            className={`rounded-4 ${errors.inTime ? "is-invalid" : ""}`}
                            style={{ height: "34px", fontSize: "12px" }}
                          />
                          {errors.inTime && <FormFeedback>{errors.inTime}</FormFeedback>}
                        </Col>
                        <Col md="1">
                          <Label className="fw-medium mb-1 text-nowrap" style={{ fontSize: "12px" }}>
                            Out <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="time"
                            value={outTime}
                            disabled={fieldsDisabled}
                            onChange={e => {
                              setOutTime(e.target.value)
                              if (errors.outTime) setErrors({ ...errors, outTime: "" })
                            }}
                            className={`rounded-4 ${errors.outTime ? "is-invalid" : ""}`}
                            style={{ height: "34px", fontSize: "12px" }}
                          />
                          {errors.outTime && <FormFeedback>{errors.outTime}</FormFeedback>}
                        </Col>
                        <Col md="2">
                          <Label className="fw-medium mb-1 text-nowrap" style={{ fontSize: "12px" }}>
                            Employees <span className="text-danger">*</span>
                          </Label>
                          <Select
                            isMulti
                            isDisabled={fieldsDisabled}
                            value={employees}
                            onChange={selected => {
                              setEmployees(selected || [])
                              if (errors.employees) setErrors({ ...errors, employees: "" })
                            }}
                            options={uniqueEmployees.map(emp => ({
                              value: emp.id,
                              label: emp.name,
                            }))}
                            className="rounded-4"
                            placeholder="Select employees..."
                            styles={{
                              control: base => ({
                                ...base,
                                minHeight: "34px",
                                borderRadius: "12px",
                                borderColor: errors.employees ? "#dc3545" : "#ced4da",
                                fontSize: "12px",
                              }),
                            }}
                          />
                          {errors.employees && <FormFeedback>{errors.employees}</FormFeedback>}
                        </Col>
                        <Col md="2">
                          <div className="d-flex gap-1">
                            <Button
                              color="success"
                              onClick={handleSaveOngoingRow}
                              className="px-3 rounded-4"
                              size="sm"
                            >
                              <FaEdit className="me-1" size={12} />
                              Save
                            </Button>
                            {editId && (
                              <Button
                                color="secondary"
                                onClick={handleCancelOngoingRow}
                                className="px-3 rounded-4"
                                size="sm"
                              >
                                <MdCancel className="me-1" size={12} />
                              </Button>
                            )}
                          </div>
                        </Col>
                      </Row>
                    )}

                    {rows.length > 0 ? (
                      <Table bordered hover responsive size="sm" style={{ tableLayout: "fixed" }}>
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: "12%" }}>Day</th>
                            <th style={{ width: "12%" }}>In Time</th>
                            <th style={{ width: "12%" }}>Out Time</th>
                            <th style={{ width: "44%" }}>Employee</th>
                            <th style={{ width: "80px" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map(row => (
                            <tr key={row.id}>
                              <td>{days.find(d => d.value === row.dayOfWeek)?.label}</td>
                              <td>{formatTo12Hour(row.inTime)}</td>
                              <td>{formatTo12Hour(row.outTime)}</td>
                              <td style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
                                {row?.employees?.map(e => e.name).join(", ") || "-"}
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <Button
                                    color="warning"
                                    size="sm"
                                    onClick={() => handleEditOngoingRow(row)}
                                  >
                                    <MdEdit size={14} />
                                  </Button>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => handleRemoveSlot(row.id)}
                                  >
                                    <MdDelete size={14} />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      !isActive && (
                        <p className="text-muted mb-0">
                          No slots added for this week yet.
                        </p>
                      )
                    )}
                  </CardBody>
                </Card>
              )
            })}
          </>
        ) : (
          <>
            <Card className="mb-0 shadow-sm">
              <CardBody className="p-4">
                <h5 className="mb-4 text-primary">
                  {editId ? "Edit Time Slot" : "Add New Time Slot"}
                </h5>

                <Row className="align-items-end g-1" style={{ flexWrap: "nowrap" }}>
                  <Col md={copyWeekEnabled ? 1 : 2}>
                    <Label className="fw-medium mb-1 text-nowrap" style={{ fontSize: "12px" }}>
                      Week <span className="text-danger">*</span>
                    </Label>
                    <select
                      value={weekNo}
                      disabled={!!editId}
                      onChange={e => {
                        const val = e.target.value
                        setWeekNo(val === "custom" ? "custom" : Number(val))
                        if (val !== "custom") setCustomWeekNo("")
                        if (errors.weekNo) setErrors({ ...errors, weekNo: "" })
                      }}
                      className={`form-select rounded-4 ${errors.weekNo ? "is-invalid" : ""}`}
                      style={{ height: "34px", fontSize: "12px" }}
                    >
                      <option value="">Select</option>
                      {WEEK_PRESET_OPTIONS.map(w => (
                        <option key={w} value={w}>
                          Week {w}
                        </option>
                      ))}
                      <option value="custom">Custom…</option>
                    </select>
                    {errors.weekNo && <FormFeedback>{errors.weekNo}</FormFeedback>}
                  </Col>
                  {weekNo === "custom" && (
                    <Col md="1">
                      <Label className="fw-medium mb-1 text-nowrap" style={{ fontSize: "12px" }}>
                        Week # <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="number"
                        min={1}
                        disabled={!!editId}
                        value={customWeekNo}
                        onChange={e => {
                          setCustomWeekNo(e.target.value)
                          if (errors.weekNo) setErrors({ ...errors, weekNo: "" })
                        }}
                        className="rounded-4"
                        style={{ height: "34px", fontSize: "12px" }}
                      />
                    </Col>
                  )}
                  <Col md="1" className="text-center">
                      <Label className="fw-medium mb-1 text-nowrap" style={{ fontSize: "12px" }}>
                        Copy
                      </Label>
                      <div
                        className="d-flex align-items-center justify-content-center rounded-4 border"
                        style={{
                          height: "34px",
                          backgroundColor: copyWeekEnabled ? "#e7f1ff" : "#fff",
                          borderColor: copyWeekEnabled ? "#0d6efd" : "#ced4da",
                          cursor: editId ? "not-allowed" : "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input m-0"
                          id="copyWeekCheck"
                          checked={copyWeekEnabled}
                          disabled={!!editId}
                          onChange={e => {
                            setCopyWeekEnabled(e.target.checked)
                            if (!e.target.checked) setSourceWeekNo("")
                            if (errors.sourceWeekNo)
                              setErrors({ ...errors, sourceWeekNo: "" })
                          }}
                          style={{
                            width: 16,
                            height: 16,
                            cursor: editId ? "not-allowed" : "pointer",
                          }}
                        />
                      </div>
                    </Col>
                  {copyWeekEnabled && (
                    <Col md="2">
                      <Label className="fw-medium mb-1 text-nowrap" style={{ fontSize: "12px" }}>
                        Target Week <span className="text-danger">*</span>
                      </Label>
                      <select
                        value={sourceWeekNo}
                        onChange={e => {
                          setSourceWeekNo(e.target.value)
                          if (errors.sourceWeekNo)
                            setErrors({ ...errors, sourceWeekNo: "" })
                        }}
                        className={`form-select rounded-4 ${errors.sourceWeekNo ? "is-invalid" : ""}`}
                        style={{ height: "34px", fontSize: "12px" }}
                      >
                        <option value="">Select</option>
                        {existingWeekNumbers
                          .filter(w => String(w) !== String(effectiveWeekNo))
                          .map(w => (
                            <option key={w} value={w}>
                              Week {w}
                            </option>
                          ))}
                      </select>
                      {errors.sourceWeekNo && (
                        <FormFeedback>{errors.sourceWeekNo}</FormFeedback>
                      )}
                    </Col>
                  )}
                  <Col md={copyWeekEnabled ? 1 : 2}>
                    <Label className="fw-medium mb-1 text-nowrap" style={{ fontSize: "12px" }}>
                      Use Existing
                    </Label>
                    <select
                      value=""
                      disabled={!!editId}
                      onChange={e => {
                        if (!e.target.value) return
                        const selected = slots.find(
                          s => String(s.id) === e.target.value
                        )
                        if (!selected) return
                        setInTime(selected.inTime)
                        setOutTime(selected.outTime)
                        setEmployees(
                          selected.employees?.map(emp => ({
                            value: emp.id,
                            label: emp.name,
                          })) || []
                        )
                      }}
                      className="form-select rounded-4"
                      style={{ height: "34px", fontSize: "12px" }}
                    >
                      <option value="">Use Existing Slot…</option>
                      {slots.map(s => (
                        <option key={s.id} value={s.id}>
                          Week {s.weekNo} - {days.find(d => d.value === s.dayOfWeek)?.label}
                        </option>
                      ))}
                    </select>
                  </Col>
                  <>
                      <Col md="1">
                        <Label className="fw-medium mb-1 text-nowrap" style={{ fontSize: "12px" }}>
                          Day <span className="text-danger">*</span>
                        </Label>
                        <select
                          value={day}
                          disabled={dayDisabled}
                          onChange={e => {
                            setday(e.target.value)
                            if (errors.day) setErrors({ ...errors, day: "" })
                          }}
                          className={`form-select rounded-4 ${errors.day ? "is-invalid" : ""}`}
                          style={{ height: "34px", fontSize: "12px" }}
                        >
                          <option value="">Select</option>
                          {days.map(d => (
                            <option key={d.value} value={d.value}>
                              {d.label}
                            </option>
                          ))}
                        </select>
                        {errors.day && <FormFeedback>{errors.day}</FormFeedback>}
                      </Col>

                      <Col md="1">
                        <Label className="fw-medium mb-1 text-nowrap" style={{ fontSize: "12px" }}>
                          In <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="time"
                          value={inTime}
                          disabled={fieldsDisabled}
                          onChange={e => {
                            setInTime(e.target.value)
                            if (errors.inTime) setErrors({ ...errors, inTime: "" })
                          }}
                          className={`rounded-4 ${errors.inTime ? "is-invalid" : ""}`}
                          style={{ height: "34px", fontSize: "12px" }}
                        />
                        {errors.inTime && <FormFeedback>{errors.inTime}</FormFeedback>}
                      </Col>
                      <Col md="1">
                        <Label className="fw-medium mb-1 text-nowrap" style={{ fontSize: "12px" }}>
                          Out <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="time"
                          value={outTime}
                          disabled={fieldsDisabled}
                          onChange={e => {
                            setOutTime(e.target.value)
                            if (errors.outTime) setErrors({ ...errors, outTime: "" })
                          }}
                          className={`rounded-4 ${errors.outTime ? "is-invalid" : ""}`}
                          style={{ height: "34px", fontSize: "12px" }}
                        />
                        {errors.outTime && <FormFeedback>{errors.outTime}</FormFeedback>}
                      </Col>
                      <Col md="2">
                        <Label className="fw-medium mb-1 text-nowrap" style={{ fontSize: "12px" }}>
                          Employees <span className="text-danger">*</span>
                        </Label>
                        <Select
                          isMulti
                          isDisabled={fieldsDisabled}
                          value={employees}
                          onChange={selected => {
                            setEmployees(selected || [])
                            if (errors.employees) setErrors({ ...errors, employees: "" })
                          }}
                          options={uniqueEmployees.map(emp => ({
                            value: emp.id,
                            label: emp.name,
                          }))}
                          className="rounded-4"
                          placeholder="Select employees..."
                          styles={{
                            control: base => ({
                              ...base,
                              minHeight: "34px",
                              borderRadius: "12px",
                              borderColor: errors.employees ? "#dc3545" : "#ced4da",
                              fontSize: "12px",
                            }),
                          }}
                        />
                        {errors.employees && <FormFeedback>{errors.employees}</FormFeedback>}
                      </Col>

                      <Col md={copyWeekEnabled ? 1 : 2} style={{ paddingTop: "22px" }}>
                        {editId ? (
                          <div className="d-flex gap-1">
                            <Button
                              color="success"
                              onClick={handleAddOrUpdateSlot}
                              className="px-3 rounded-4"
                              size="sm"
                            >
                              <FaEdit className="me-1" size={12} />
                              Update
                            </Button>
                            <Button
                              color="secondary"
                              onClick={handleCancelEdit}
                              className="px-3 rounded-4"
                              size="sm"
                            >
                              <MdCancel className="me-1" size={12} />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            color="primary"
                            onClick={handleAddOrUpdateSlot}
                            className="px-3 rounded-4"
                            size="sm"
                          >
                            <FaPlus className="me-1" size={12} />
                            Add
                          </Button>
                        )}
                      </Col>
                    </>
                </Row>
              </CardBody>
            </Card>

            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-3 text-primary">Existing Time Slots</h5>
              </div>

              {slots.length > 0 ? (
                <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                  <Table bordered hover responsive size="sm" style={{ tableLayout: "fixed" }}>
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "35px" }}>#</th>
                        <th style={{ width: "10%" }}>Week</th>
                        <th style={{ width: "10%" }}>Day</th>
                        <th style={{ width: "10%" }}>In Time</th>
                        <th style={{ width: "10%" }}>Out Time</th>
                        <th style={{ width: "10%" }}>Duration</th>
                        <th style={{ width: "28%" }}>Employees</th>
                        <th style={{ width: "80px" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {slots.map((slot, index) => (
                        <tr key={slot.id}>
                          <td>{index + 1}</td>
                          <td>Week {slot.weekNo}</td>
                          <td>{days.find(d => d.value === slot.dayOfWeek)?.label}</td>
                          <td>{formatTo12Hour(slot.inTime)}</td>
                          <td>{formatTo12Hour(slot.outTime)}</td>
                          <td>{formatDuration(slot.inTime, slot.outTime)}</td>
                          <td style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
                            {slot?.employees?.map(e => e.name).join(", ") || "-"}
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                color="warning"
                                size="sm"
                                onClick={() => handleEditSlot(slot)}
                              >
                                <MdEdit size={14} />
                              </Button>
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => handleRemoveSlot(slot.id)}
                              >
                                <MdDelete size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <p className="text-muted">No time slots added yet.</p>
              )}
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  )
}
export default LocationTimeModal
